import React, { useState } from "react";
import { Link } from "react-router-dom";
import ConsultancyNavbar from "../ConsultancyNavbar";
import ConsultancyFooter from "../ConsultancyFooter";

const BookConsultationPage = () => {
  const todayStr = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    agencyName: "",
    contactName: "",
    email: "",
    phone: "",
    city: "",
    branches: "1 Office",
    studentVolume: "50 - 200 Students / Year",
    targetCountries: "United Kingdom & USA",
    modulesNeeded: "Counselling CRM & Lead Pipeline",
    staffCount: "5 - 15 Staff Members",
    preferredDemoDate: todayStr,
    preferredTimeSlot: "Morning (10:00 AM - 1:00 PM)",
    notes: "",
  });

  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    window.scrollTo({ top: 120, behavior: "smooth" });
  };

  const resetForm = () => {
    setForm({
      agencyName: "",
      contactName: "",
      email: "",
      phone: "",
      city: "",
      branches: "1 Office",
      studentVolume: "50 - 200 Students / Year",
      targetCountries: "United Kingdom & USA",
      modulesNeeded: "Counselling CRM & Lead Pipeline",
      staffCount: "5 - 15 Staff Members",
      preferredDemoDate: todayStr,
      preferredTimeSlot: "Morning (10:00 AM - 1:00 PM)",
      notes: "",
    });
    setSubmitted(false);
  };

  const featureHighlights = [
    {
      icon: "fa-users",
      title: "Lead CRM & Counsellor Pipeline",
      desc: "Auto-capture student inquiries, assign to counsellors, and track follow-ups without spreadsheet chaos.",
    },
    {
      icon: "fa-university",
      title: "Course & University Finder",
      desc: "Explore 1,200+ global partner universities, entry requirements, intakes, and tuition fees in real time.",
    },
    {
      icon: "fa-paper-plane",
      title: "End-to-End Application Pipeline",
      desc: "Track student stages from document review and university submission to offer letters and visa approval.",
    },
    {
      icon: "fa-calculator",
      title: "Automated Commission Engine",
      desc: "Automate sub-agent commission payouts, university billing, and currency calculations seamlessly.",
    },
    {
      icon: "fa-chart-pie",
      title: "Multi-Branch & Executive Analytics",
      desc: "Monitor live branch revenue, conversion rates, and counsellor performance leaderboards.",
    },
  ];

  const demoAgenda = [
    {
      time: "00 - 05m",
      title: "Agency Operations & Discovery",
      desc: "Audit current spreadsheet or CRM pain points across your branch offices.",
    },
    {
      time: "05 - 20m",
      title: "Personalized Live Platform Walkthrough",
      desc: "Deep dive into Counsellor Pipeline, Course Finder, and Visa Tracker tailored to your volume.",
    },
    {
      time: "20 - 30m",
      title: "Custom Pricing & Data Migration Plan",
      desc: "Review seamless 1-click student data migration, sub-agent logins, and launch timeline.",
    },
  ];

  return (
    <div className="consultancy-wrapper">
      <ConsultancyNavbar />

      {/* Header */}
      <section className="py-5 consultancy-section-dark text-center">
        <div className="container">
          <div className="mb-2">
            <span className="consultancy-badge-gold">
              <i className="fas fa-desktop mr-2"></i> Live 1-on-1 Platform Walkthrough
            </span>
          </div>
          <h1 className="display-5 font-weight-bold consultancy-gold-title mb-2">
            Schedule Your Personalized EdOps OS Demo
          </h1>
          <p className="consultancy-lead-text mx-auto mb-0" style={{ maxWidth: "720px" }}>
            Experience how EdOps OS centralizes leads, counsellors, applications, universities, and commissions into a single intelligent platform.
          </p>
        </div>
      </section>

      {/* Main Single-Page Content */}
      <section className="py-5 container">
        <div className="row g-4 align-items-stretch">
          {/* Left Column: Why Book a Demo & Platform Highlights (Unified Full Height) */}
          <div className="col-lg-5 col-xl-5">
            <div className="consultancy-glass-card h-100 p-4 d-flex flex-column justify-content-between">
              <div>
                <div className="mb-2">
                  <span className="consultancy-badge-pill">
                    <i className="fas fa-bolt text-warning mr-1"></i> 30-Minute Focused Session
                  </span>
                </div>
                <h3 className="consultancy-gold-title font-weight-bold fs-4 mb-2">
                  What You'll Experience in the Demo:
                </h3>
                <p className="text-muted small mb-3">
                  Our SaaS enterprise team will guide you through a live walkthrough tailored specifically to your agency's scale, student volume, and branch operations.
                </p>

                {/* Feature Highlights List */}
                <div className="mb-3">
                  {featureHighlights.map((feat, idx) => (
                    <div key={idx} className="consultancy-feature-box d-flex align-items-start gap-3 py-2 px-3 mb-2">
                      <div className="consultancy-feature-icon">
                        <i className={`fas ${feat.icon}`}></i>
                      </div>
                      <div>
                        <h6 className="text-white font-weight-bold mb-1 small">{feat.title}</h6>
                        <p className="text-muted mb-0" style={{ fontSize: "0.8rem", lineHeight: "1.35" }}>
                          {feat.desc}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Stat Strip */}
                <div className="consultancy-stat-strip mb-3">
                  <div className="consultancy-stat-strip-item">
                    <h5>150+</h5>
                    <span>Consultancies</span>
                  </div>
                  <div className="consultancy-stat-strip-item">
                    <h5>3x</h5>
                    <span>Faster Filing</span>
                  </div>
                  <div className="consultancy-stat-strip-item">
                    <h5>99.4%</h5>
                    <span>Visa Accuracy</span>
                  </div>
                </div>

                {/* Demo Session Agenda Breakdown */}
                <div className="consultancy-agenda-box mb-3">
                  <h6 className="consultancy-gold-title font-weight-bold mb-2 small d-flex align-items-center gap-2">
                    <i className="fas fa-clock text-warning"></i> 30-Minute Session Breakdown:
                  </h6>
                  {demoAgenda.map((item, idx) => (
                    <div key={idx} className="consultancy-agenda-step">
                      <span className="consultancy-agenda-time">{item.time}</span>
                      <div>
                        <div className="text-white font-weight-bold" style={{ fontSize: "0.8rem" }}>
                          {item.title}
                        </div>
                        <div className="text-muted" style={{ fontSize: "0.74rem", lineHeight: "1.3" }}>
                          {item.desc}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Agency Director Testimonials */}
                <div className="consultancy-testimonial-box mb-2">
                  <p className="text-light italic small mb-1" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                    "EdOps OS centralized our 4 branch offices and boosted our application turnaround time by 3x in our first intake."
                  </p>
                  <div className="text-warning small font-weight-bold" style={{ fontSize: "0.75rem" }}>
                    — Vikram Malhotra, Managing Director (Apex Global Overseas)
                  </div>
                </div>

                <div className="consultancy-testimonial-box mb-3">
                  <p className="text-light italic small mb-1" style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                    "Automated commission tracking and counsellor task reminders alone saved our operations team 15+ hours every week."
                  </p>
                  <div className="text-warning small font-weight-bold" style={{ fontSize: "0.75rem" }}>
                    — Sneha Kulkarni, Operations Head (Global Pathway Consultants)
                  </div>
                </div>
              </div>

              {/* Integrated Card Footer */}
              <div className="consultancy-card-integrated-footer">
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 text-warning small font-weight-bold mb-2">
                  <div className="d-flex align-items-center gap-1">
                    <i className="fas fa-star"></i>
                    <i className="fas fa-star"></i>
                    <i className="fas fa-star"></i>
                    <i className="fas fa-star"></i>
                    <i className="fas fa-star"></i>
                    <span className="text-white ml-1">4.9/5 Rating</span>
                  </div>
                  <span className="text-muted small">
                    <i className="fas fa-shield-alt text-success mr-1"></i> 100% Free • Enterprise NDA
                  </span>
                </div>

                <a
                  href="https://wa.me/919876543210?text=Hi%20EdOps%20OS%20Team,%20I%20would%20like%20to%20know%20more%20about%20the%20platform"
                  target="_blank"
                  rel="noreferrer"
                  className="consultancy-btn-outline-gold w-100 py-2 text-decoration-none small d-flex align-items-center justify-content-center gap-2"
                >
                  <i className="fab fa-whatsapp text-success fs-6"></i> Speak Directly: +91 98765 43210
                </a>
              </div>
            </div>
          </div>

          {/* Right Column: Unified Single-Page Form / Confirmation */}
          <div className="col-lg-7 col-xl-7">
            <div className="consultancy-glass-card h-100 p-4 p-md-5 border-warning">


              {submitted ? (
                /* Success Confirmation State */
                <div className="text-center py-4">
                  <div
                    className="avatar bg-soft-success text-success p-4 rounded-circle mx-auto mb-3"
                    style={{
                      width: "80px",
                      height: "80px",
                      display: "flex",
                      flexShrink: 0,
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <i className="fas fa-check-circle fa-3x"></i>
                  </div>
                  <h2 className="display-6 font-weight-bold consultancy-gold-title mb-2">
                    Demo Session Successfully Booked! 🎉
                  </h2>
                  <p className="text-muted lead mb-4" style={{ fontSize: "1rem" }}>
                    Thank you <strong>{form.contactName}</strong> from <strong>{form.agencyName}</strong>. Our enterprise team has reserved your live walkthrough.
                  </p>

                  {/* Summary Card */}
                  <div className="p-4 bg-black rounded border border-secondary text-start mb-4 text-muted small">
                    <h6 className="consultancy-gold-title font-weight-bold mb-3 d-flex align-items-center gap-2">
                      <i className="fas fa-calendar-check text-warning"></i>
                      Demo Reservation Details:
                    </h6>
                    <div className="row g-2">
                      <div className="col-sm-6">
                        <span className="text-muted">Agency Name:</span>{" "}
                        <strong className="text-warning d-block">{form.agencyName}</strong>
                      </div>
                      <div className="col-sm-6">
                        <span className="text-muted">Contact Person:</span>{" "}
                        <strong className="text-white d-block">{form.contactName}</strong>
                      </div>
                      <div className="col-sm-6">
                        <span className="text-muted">Work Email:</span>{" "}
                        <strong className="text-white d-block">{form.email}</strong>
                      </div>
                      <div className="col-sm-6">
                        <span className="text-muted">Mobile / WhatsApp:</span>{" "}
                        <strong className="text-white d-block">{form.phone}</strong>
                      </div>
                      <div className="col-sm-6">
                        <span className="text-muted">Scheduled Date:</span>{" "}
                        <strong className="text-warning d-block">{form.preferredDemoDate}</strong>
                      </div>
                      <div className="col-sm-6">
                        <span className="text-muted">Preferred Time Slot:</span>{" "}
                        <strong className="text-warning d-block">{form.preferredTimeSlot}</strong>
                      </div>
                      <div className="col-sm-6">
                        <span className="text-muted">Student Volume:</span>{" "}
                        <strong className="text-white d-block">{form.studentVolume}</strong>
                      </div>
                      <div className="col-sm-6">
                        <span className="text-muted">Key Focus Module:</span>{" "}
                        <strong className="text-white d-block">{form.modulesNeeded}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Next Steps Timeline */}
                  <div className="p-3 bg-black rounded border border-secondary text-start mb-4 text-muted small">
                    <h6 className="text-white font-weight-bold mb-2">What Happens Next?</h6>
                    <ul className="pl-3 mb-0" style={{ lineHeight: "1.7" }}>
                      <li>
                        A calendar invite with Google Meet link will be sent to <strong className="text-warning">{form.email}</strong>.
                      </li>
                      <li>
                        Our SaaS specialist will send a quick WhatsApp confirmation to <strong className="text-warning">{form.phone}</strong>.
                      </li>
                      <li>
                        Enjoy a customized 30-minute platform demo solving your exact agency challenges.
                      </li>
                    </ul>
                  </div>

                  <div className="d-flex justify-content-center gap-3 flex-wrap">
                    <Link to="/" className="consultancy-btn-gold px-4 py-3">
                      <i className="fas fa-home mr-1"></i> Return to Home
                    </Link>
                    <button
                      type="button"
                      className="consultancy-btn-outline-gold px-4 py-3"
                      onClick={resetForm}
                    >
                      <i className="fas fa-redo mr-1"></i> Book Another Demo
                    </button>
                  </div>
                </div>
              ) : (
                /* Unified Single-Page Form */
                <form onSubmit={handleSubmit}>
                  <div className="mb-4 pb-3 border-bottom border-secondary">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                      <span className="consultancy-badge-pill mb-0">
                        <i className="fas fa-check-circle text-warning mr-1"></i> Single-Step Demo Booking
                      </span>
                    </div>
                    <h3 className="consultancy-gold-title font-weight-bold fs-4 mb-1">
                      Book Your Live Platform Demo
                    </h3>
                    <p className="text-muted small mb-0">
                      Fill in your agency details below to instantly reserve your 1-on-1 walkthrough.
                    </p>
                  </div>


                  {/* Section 1: Contact & Agency Info */}
                  <div className="consultancy-form-section">
                    <div className="consultancy-section-subheading">
                      <i className="fas fa-building text-warning"></i>
                      <span>1. Agency & Contact Information</span>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Consultancy / Agency Name *</label>
                        <input
                          type="text"
                          className="form-control consultancy-form-control"
                          placeholder="e.g. Global Scholars Overseas"
                          value={form.agencyName}
                          onChange={(e) => setForm({ ...form, agencyName: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Director / Contact Person Name *</label>
                        <input
                          type="text"
                          className="form-control consultancy-form-control"
                          placeholder="e.g. Rajesh Sharma"
                          value={form.contactName}
                          onChange={(e) => setForm({ ...form, contactName: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Official Work Email *</label>
                        <input
                          type="email"
                          className="form-control consultancy-form-control"
                          placeholder="director@globalscholars.com"
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Mobile / WhatsApp Number *</label>
                        <input
                          type="tel"
                          className="form-control consultancy-form-control"
                          placeholder="+91 98765 43210"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Headquarters City *</label>
                        <input
                          type="text"
                          className="form-control consultancy-form-control"
                          placeholder="e.g. Hyderabad, India"
                          value={form.city}
                          onChange={(e) => setForm({ ...form, city: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Total Offices / Branches</label>
                        <select
                          className="form-select consultancy-form-control"
                          value={form.branches}
                          onChange={(e) => setForm({ ...form, branches: e.target.value })}
                        >
                          <option value="1 Office">Single Office</option>
                          <option value="2 - 5 Branches">2 - 5 Branch Offices</option>
                          <option value="6+ Franchises">6+ Franchise Network</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Operations & Module Requirements */}
                  <div className="consultancy-form-section">
                    <div className="consultancy-section-subheading">
                      <i className="fas fa-sliders-h text-warning"></i>
                      <span>2. Agency Profile & Key Focus Areas</span>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Annual Student Volume</label>
                        <select
                          className="form-select consultancy-form-control"
                          value={form.studentVolume}
                          onChange={(e) => setForm({ ...form, studentVolume: e.target.value })}
                        >
                          <option value="< 50 Students / Year">&lt; 50 Students / Year</option>
                          <option value="50 - 200 Students / Year">50 - 200 Students / Year</option>
                          <option value="200 - 500 Students / Year">200 - 500 Students / Year</option>
                          <option value="500+ Students / Year">500+ Students / Year</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Primary Study Destinations</label>
                        <select
                          className="form-select consultancy-form-control"
                          value={form.targetCountries}
                          onChange={(e) => setForm({ ...form, targetCountries: e.target.value })}
                        >
                          <option value="United Kingdom & USA">United Kingdom & USA</option>
                          <option value="Canada & Australia">Canada & Australia</option>
                          <option value="Germany & Europe">Germany & Europe</option>
                          <option value="All Global Markets">All Global Markets</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Primary Module of Interest</label>
                        <select
                          className="form-select consultancy-form-control"
                          value={form.modulesNeeded}
                          onChange={(e) => setForm({ ...form, modulesNeeded: e.target.value })}
                        >
                          <option value="Counselling CRM & Lead Pipeline">Counselling CRM & Lead Pipeline</option>
                          <option value="End-to-End Application Pipeline">End-to-End Application Pipeline</option>
                          <option value="University Commission Engine">University Commission Engine</option>
                          <option value="Multi-Branch Analytics Suite">Multi-Branch Analytics Suite</option>
                          <option value="Complete All-in-One EdOps OS">Complete All-in-One EdOps OS</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Counsellor & Staff Team Size</label>
                        <select
                          className="form-select consultancy-form-control"
                          value={form.staffCount}
                          onChange={(e) => setForm({ ...form, staffCount: e.target.value })}
                        >
                          <option value="1 - 5 Counsellors">1 - 5 Counsellors</option>
                          <option value="5 - 15 Staff Members">5 - 15 Staff Members</option>
                          <option value="15 - 30 Team Members">15 - 30 Team Members</option>
                          <option value="30+ Enterprise Network">30+ Enterprise Network</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Scheduling & Preferences */}
                  <div className="consultancy-form-section">
                    <div className="consultancy-section-subheading">
                      <i className="fas fa-calendar-alt text-warning"></i>
                      <span>3. Demo Schedule & Preferences</span>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Preferred Demo Date *</label>
                        <input
                          type="date"
                          min={todayStr}
                          className="form-control consultancy-form-control"
                          value={form.preferredDemoDate}
                          onChange={(e) => setForm({ ...form, preferredDemoDate: e.target.value })}
                          required
                        />
                      </div>
                      <div className="col-md-6">
                        <label className="small text-muted font-weight-bold mb-1">Preferred Time Slot</label>
                        <select
                          className="form-select consultancy-form-control"
                          value={form.preferredTimeSlot}
                          onChange={(e) => setForm({ ...form, preferredTimeSlot: e.target.value })}
                        >
                          <option value="Morning (10:00 AM - 1:00 PM)">Morning (10:00 AM - 1:00 PM IST)</option>
                          <option value="Afternoon (2:00 PM - 5:00 PM)">Afternoon (2:00 PM - 5:00 PM IST)</option>
                          <option value="Evening (5:00 PM - 8:00 PM)">Evening (5:00 PM - 8:00 PM IST)</option>
                        </select>
                      </div>
                      <div className="col-12">
                        <label className="small text-muted font-weight-bold mb-1">
                          Specific Requirements / Current Pain Points <span className="text-secondary">(Optional)</span>
                        </label>
                        <textarea
                          rows={2}
                          className="form-control consultancy-form-control"
                          placeholder="e.g. We currently use Excel and want to streamline our 4 branch offices and university application submissions..."
                          value={form.notes}
                          onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        ></textarea>
                      </div>
                    </div>
                  </div>

                  {/* Submit Action */}
                  <div className="mt-4 pt-2">
                    <button type="submit" className="consultancy-btn-gold w-100 py-3 font-weight-bold fs-6">
                      Schedule Live 1-on-1 Demo 🚀
                    </button>
                    <div className="text-center text-muted small mt-3 d-flex align-items-center justify-content-center gap-2">
                      <i className="fas fa-lock text-warning"></i>
                      <span>Your information is 100% confidential. No spam, ever.</span>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <ConsultancyFooter />
    </div>
  );
};

export default BookConsultationPage;

